import dayjs from 'dayjs'

/**
 * DISPLAY-ONLY date formatting for Report Center (Himaad UI feedback, 2026-08-27).
 *
 * Every date a human READS in Report Center chrome — filter pills, export criteria
 * lines, provenance stamps, "as of" labels — is MM/DD/YYYY, matching UsDateInput
 * (`src/components/common/UsDateInput.jsx`), which has forced US format on the
 * filter inputs since CC-49824. Nothing produced here ever reaches CB.
 *
 * ⚠️ THE WIRE COUNTERPART IS `src/utils/cbDateSettings.js:56` — `fmt()`, which emits
 * the 'YYYY-MM-DD' halves of the pipe-delimited "YYYY-MM-DD|YYYY-MM-DD" range CB
 * requires (or the "$$cb_all" macro). This module lives in its own file precisely so
 * the two cannot be confused: a display format leaking into that formatter would not
 * error — CB's stored proc would re-scope the query and hand back a confidently wrong
 * WIP or A/R number. If you are reaching for a formatter inside a filter-wire path,
 * you want the other file, not this one.
 *
 * ⚠️ REPORT GRID CELLS ARE DELIBERATELY NOT FED THROUGH HERE (product-owner decision,
 * 2026-08-27). On the 119 generic reports CB formats each date cell server-side from
 * that column's CellsFormat; at least two formats ship today, and a non-US tenant
 * culture already receives dd/MM/yyyy — so a client reformat could swap month and day
 * with no error anywhere. It would also break
 * `src/components/reports/reportGridUtils.js` `parseCellDate`/`isStaleValue` (an
 * UNPARSEABLE cell counts as STALE, so every row would match an aging filter) and
 * `groupFlatReport`, which keys its group buckets off the display string. The one
 * exception is a page that SYNTHESIZES its own cells — ARReport builds its own
 * `displayValue` — and those cells are cb2's to format.
 *
 * Parsing is dayjs's lenient constructor on purpose: callers pass ISO dates from the
 * wire, ISO datetimes from D1, `Date` objects and epoch numbers. Note that dayjs reads
 * a bare 'YYYY-MM-DD' as LOCAL midnight, unlike `new Date(...)`, which reads it as UTC
 * and therefore renders the PREVIOUS day west of Greenwich — do not swap this for
 * `toLocaleDateString` without re-checking that.
 */

// dayjs(undefined) is NOW, not an invalid date, so the empty guard is load-bearing
// rather than tidiness. Deliberately `== null || === ''` and not a falsy check: a
// numeric 0 is a real epoch and renders as 12/31/1969, which is absurd enough on a law
// firm's report to surface the caller's bug instead of hiding it behind a blank.
const parse = (v) => {
  if (v == null || v === '') return null
  const d = dayjs(v)
  return d.isValid() ? d : null
}

/**
 * MM/DD/YYYY. Returns '' for null/undefined/unparseable.
 *
 * '' rather than echoing the input: a half-formatted or foreign-shaped date sitting in
 * a financial report's criteria line invites the reader to interpret it, and
 * "07/31/2026", "31/07/2026" and "2026-07-31" are not distinguishable at a glance for
 * the first twelve days of any month. A blank is visibly missing information; a
 * plausible-looking wrong date is worse. There is also nothing to preserve for the
 * server — this helper is never in a wire path (see the wire note above).
 */
export const fmtDisplayDate = (v) => parse(v)?.format('MM/DD/YYYY') ?? ''

/**
 * MM/DD/YYYY h:mm A — e.g. "08/27/2026 2:59 PM". Blank on the same terms as
 * fmtDisplayDate, for the same reason.
 */
export const fmtDisplayDateTime = (v) => parse(v)?.format('MM/DD/YYYY h:mm A') ?? ''
